// region Imports
import React, { Component } from "react";
import PropTypes from "prop-types";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { ToastProvider } from "react-toast-notifications";
import _ from "lodash";
import detectBrowserLanguage from "detect-browser-language";
import ToastNotification from "./Components/Common/ToastNotification";
import LoginPage from "./Pages/Login/Login";
import PasswordRecoveryPage from "./Pages/Login/PasswordRecovery";
import PasswordResetPage from "./Pages/Login/PasswordReset";
import HomePage from "./Pages/HomePage";
import Kits from "./Pages/Products/Kits";
import KitBuild from "./Pages/Products/KitBuild";
import Rentals from "./Pages/Products/Rentals";
import FeaturedProducts from "./Pages/Products/FeaturedProducts";
import Devices from "./Pages/Products/Devices";
import VtrackItems from "./Pages/Products/VtrackItems";
import LineHaulItems from "./Pages/Products/LineHaulItems";
import ProductDetails from "./Pages/Products/ProductDetails";
import ZebraAccessories from "./Pages/Products/Accessories/ZebraAccessories";
import ZebraTC26Accessories from "./Pages/Products/Accessories/ZebraTC26Accessories";
import ZebraTC58Accessories from "./Pages/Products/Accessories/ZebraTC58Accessories";
import ZebraTC78Accessories from "./Pages/Products/Accessories/ZebraTC78Accessories";
import PanasonicAccessories from "./Pages/Products/Accessories/PanasonicAccessories";
import PanasonicT1Accessories from "./Pages/Products/Accessories/PanasonicT1Accessories";
import SamsungAccessories from "./Pages/Products/Accessories/SamsungAccessories";
import UniversalAccessories from "./Pages/Products/Accessories/UniversalAccessories";
import Support from "./Pages/Support";
import Services from "./Pages/Topics/Services";
import Company from "./Pages/Topics/Company";
import LeasingFAQs from "./Pages/Topics/LeasingFAQs";
import PrivacyPolicy from "./Pages/Topics/PrivacyPolicy";
import ReturnRefundPolicy from "./Pages/Topics/ReturnRefundPolicy";
import AdvancedExchange from "./Pages/Topics/AdvancedExchange";
import BuyingGuide from "./Pages/Topics/BuyingGuide";
import PaymentOptions from "./Pages/Topics/PaymentOptions";
import Cart from "./Pages/Checkout/Cart";
import Checkout from "./Pages/Checkout/PayInFull/Checkout";
import ApplyForLease from "./Pages/Checkout/Lease/ApplyForLease";
import ProcessLease from "./Pages/Checkout/Lease/ProcessLease";
import OrderComplete from "./Pages/Checkout/OrderComplete";
import DeviceComparison from "./Pages/DeviceComparison";
import Customer from "./Pages/Account/Customer";
import AuthenticatedRoute from "./HOC/AuthenticatedRoute";
import UnauthenticatedRoute from "./HOC/UnauthenticatedRoute";
import AccountProfileRoute from "./HOC/AccountProfileRoute";
import { loadConfig } from "./Store/Config/Actions";
import { getUserInfo, setAuthenticated, validatePasswordResetRequest } from "./Store/Account/Actions";
import { getLanguages, getCurrencies, setLanguage, setCurrency } from "./Store/Localization/Actions";
import ScrollToTop from "./HOC/ScrollToTop";
import Constants from "./AppConstants/Constants";
// endregion

const userLanguage = detectBrowserLanguage();

class Router extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loadedConfig: false,
            isAuthenticating: true
        };

        if (Object.keys(props.config).length < 1) {
            props.loadConfig(() => {
                this.setState({ loadedConfig: true });
            });
        }
    }

    componentDidMount() {
        const { getLanguages, getCurrencies, getUserInfo, setAuthenticated, setLanguage, setCurrency } = this.props;

        getCurrencies();
        getLanguages(languages => {
            getUserInfo(data => {
                if (data && (data.firstName || data.lastName || data.employeeId)) {
                    setAuthenticated(true);
                    setLanguage({ language: data.language });
                    setCurrency({ currency: data.currency });
                } else {
                    const lng = _.find(languages, lg => lg.languageCulture === userLanguage);

                    if (lng) {
                        setLanguage({ language: lng });
                    }
                }

                this.setState({ isAuthenticating: false });
            });
        });
    }

    render() {
        const { isAuthenticated, validatePasswordResetRequest } = this.props;
        const { loadedConfig, isAuthenticating } = this.state;

        const childProps = { isAuthenticated };

        return (
            loadedConfig && !isAuthenticating &&
            <BrowserRouter>
                <Switch>
                    <ToastProvider
                        transitionDuration={500}
                        components={{
                            ToastContainer: ToastNotification
                        }}
                    >
                        <ScrollToTop>
                            <UnauthenticatedRoute exact path={Constants.ENDPOINTS.LOGIN} component={LoginPage} props={childProps} />
                            <UnauthenticatedRoute exact path={Constants.ENDPOINTS.PASSWORD_RECOVERY} component={PasswordRecoveryPage} props={childProps} />
                            <UnauthenticatedRoute exact path={Constants.ENDPOINTS.PASSWORD_RESET} component={PasswordResetPage} props={{ validatePasswordResetRequest }} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.HOME} component={HomePage} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.KITS} component={Kits} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.KITS_DETAILS} component={ProductDetails} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.BUILD_A_KIT} component={KitBuild} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.RENTALS} component={Rentals} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.RENTALS_DETAILS} component={ProductDetails} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.FEATURED_PRODUCTS} component={FeaturedProducts} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.DEVICES} component={Devices} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.DEVICES_DETAILS} component={ProductDetails} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.ZEBRA_ACCESSORIES} component={ZebraAccessories} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.ZEBRA_TC26_ACCESSORIES} component={ZebraTC26Accessories} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.ZEBRA_TC58_ACCESSORIES} component={ZebraTC58Accessories} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.ZEBRA_TC78_ACCESSORIES} component={ZebraTC78Accessories} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.PANASONIC_ACCESSORIES} component={PanasonicAccessories} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.PANASONIC_T1_ACCESSORIES} component={PanasonicT1Accessories} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.SAMSUNG_ACCESSORIES} component={SamsungAccessories} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.UNIVERSAL_ACCESSORIES} component={UniversalAccessories} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.ZEBRA_DETAILS} component={ProductDetails} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.ZEBRA_TC26_DETAILS} component={ProductDetails} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.ZEBRA_TC58_DETAILS} component={ProductDetails} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.ZEBRA_TC78_DETAILS} component={ProductDetails} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.PANASONIC_DETAILS} component={ProductDetails} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.PANASONIC_T1_DETAILS} component={ProductDetails} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.SAMSUNG_DETAILS} component={ProductDetails} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.UNIVERSAL_DETAILS} component={ProductDetails} props={childProps} />
                            <Route exact path={Constants.ENDPOINTS.SUPPORT} component={Support} />
                            <Route exact path={Constants.ENDPOINTS.SERVICES} component={Services} />
                            <Route exact path={Constants.ENDPOINTS.COMPANY} component={Company} />
                            <Route exact path={Constants.ENDPOINTS.LEASING_FAQS} component={LeasingFAQs} />
                            <Route exact path={Constants.ENDPOINTS.RETURN_REFUND_POLICY} component={ReturnRefundPolicy} />
                            <Route exact path={Constants.ENDPOINTS.ADVANCED_EXCHANGE} component={AdvancedExchange} />
                            <Route exact path={Constants.ENDPOINTS.PRIVACY_POLICY} component={PrivacyPolicy} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.VTRACK} component={VtrackItems} props={childProps} />
                            <AuthenticatedRoute exact path={Constants.ENDPOINTS.LINEHAUL} component={LineHaulItems} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.VTRACK_DETAILS} component={ProductDetails} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.LINEHAUL_DETAILS} component={ProductDetails} props={childProps} />
                            <Route path={Constants.ENDPOINTS.BUYING_GUIDE} component={BuyingGuide} />
                            <Route path={Constants.ENDPOINTS.PAYMENT_OPTIONS} component={PaymentOptions} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.CART} component={Cart} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.PAY_IN_FULL_CHECKOUT} component={Checkout} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.LEASE_APPLICATION} component={ApplyForLease} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.LEASE_CHECKOUT} component={ProcessLease} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.ORDER_CONFIRMATION} component={OrderComplete} props={childProps} />
                            <AuthenticatedRoute path={Constants.ENDPOINTS.DEVICE_COMPARISON} component={DeviceComparison} props={childProps} />
                            <AccountProfileRoute path={Constants.ENDPOINTS.CUSTOMER} component={Customer} props={childProps} />
                        </ScrollToTop>
                    </ToastProvider>
                </Switch>
            </BrowserRouter>
        );
    }
}

Router.propTypes = {
    loadConfig: PropTypes.func,
    getUserInfo: PropTypes.func,
    getLanguages: PropTypes.func,
    getCurrencies: PropTypes.func,
    setLanguage: PropTypes.func,
    setCurrency: PropTypes.func,
    setAuthenticated: PropTypes.func,
    validatePasswordResetRequest: PropTypes.func,
    config: PropTypes.object,
    isAuthenticated: PropTypes.bool
};

function mapStateToProps(state) {
    return {
        config: state.config,
        isAuthenticated: state.isAuthenticated
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        loadConfig: loadConfig,
        getUserInfo: getUserInfo,
        getLanguages: getLanguages,
        getCurrencies: getCurrencies,
        setLanguage: setLanguage,
        setCurrency: setCurrency,
        setAuthenticated: setAuthenticated,
        validatePasswordResetRequest: validatePasswordResetRequest
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Router);
